import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  Box,
  Button,
  Stepper,
  Step,
  StepLabel,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

// Modal Base
import {
  AnimatedModal,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "../modals/AnimatedModal";

// Redux
import {
  setShowOnboardingModal,
  setCurrentStep,
  resetOnboarding,
} from "../../store/actions/onboardingSlice";

// Utilities
import { canManage } from "../../common/validators";

// Utilities (onboarding)
import { markOnboardingComplete } from "../../utils/onboarding";
import { dispatchSnackBarMessage } from "../../utils/snackbar";

// Content & Styles
import { steps } from "./content";
import StepContent from "./StepContent";

// Step Components
import WelcomeStep from "./steps/WelcomeStep";
import MenuProductsStep from "./steps/MenuProductsStep";
import OperationsStep from "./steps/OperationsStep";
import DeliveryTeamStep from "./steps/DeliveryTeamStep";
import RoutePricingStep from "./steps/RoutePricingStep";
import IntegrationsStep from "./steps/IntegrationsStep";
import FiscalFinancialStep from "./steps/FiscalFinancialStep";
import FinalStep from "./steps/FinalStep";

// Styled Components
import { ModernButton } from "../styled/button";

// Animation variants
const stepVariants = {
  enter: (direction) => ({
    x: direction > 0 ? 300 : -300,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
    transition: { duration: 0.4, ease: "easeInOut" },
  },
  exit: (direction) => ({
    x: direction < 0 ? 300 : -300,
    opacity: 0,
    transition: { duration: 0.3 },
  }),
};

const OnboardingGuidanceModal = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const user = useSelector((state) => state.auth.user);
  const userType = useSelector((state) => state.auth.user?.user_type);
  const currentStepRedux = useSelector((state) => state.onboarding.currentStep);

  const userCanManage = canManage(userType);

  const visibleSteps = steps
    .map((s, i) => ({ ...s, originalIndex: i }))
    .filter((s) => !s.requiresManager || userCanManage);

  const [activeStep, setActiveStep] = useState(currentStepRedux || 0);
  const [direction, setDirection] = useState(0);

  const handleNext = () => {
    if (activeStep === visibleSteps.length - 1) {
      handleComplete();
    } else {
      setDirection(1);
      const nextStep = activeStep + 1;
      setActiveStep(nextStep);
      dispatch(setCurrentStep(nextStep));
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setDirection(-1);
      const prevStep = activeStep - 1;
      setActiveStep(prevStep);
      dispatch(setCurrentStep(prevStep));
    }
  };

  const handleSkip = () => {
    if (window.confirm("Você pode acessar todas essas configurações a qualquer momento em Central de Entrega. Deseja pular o tutorial?")) {
      handleComplete();
    }
  };

  const handleComplete = async () => {
    try {
      // Mark as completed via API
      await markOnboardingComplete();

      // Update Redux
      dispatch(setShowOnboardingModal(false));
      dispatch(resetOnboarding());

      // Show success message
      dispatchSnackBarMessage(
        "onboarding-complete",
        "Parabéns! Você está pronto para usar o Slapie.",
        "success"
      );
    } catch (err) {
      console.error("Error completing onboarding:", err);

      // Show error message but still close
      dispatchSnackBarMessage(
        "onboarding-complete-error",
        "Erro ao salvar progresso, mas você pode continuar usando o Slapie.",
        "warning"
      );

      // Close modal anyway
      dispatch(setShowOnboardingModal(false));
      dispatch(resetOnboarding());
    }
  };

  const handleNavigateToConfig = async (route) => {
    try {
      // Mark as completed via API
      await markOnboardingComplete();

      // Close modal
      dispatch(setShowOnboardingModal(false));
      dispatch(resetOnboarding());

      // Navigate
      history.push(route);
    } catch (err) {
      console.error("Error completing onboarding:", err);

      // Still navigate even if API fails
      dispatch(setShowOnboardingModal(false));
      dispatch(resetOnboarding());
      history.push(route);
    }
  };

  const renderStep = () => {
    const originalIndex = visibleSteps[activeStep]?.originalIndex;
    switch (originalIndex) {
      case 0:
        return <WelcomeStep />;
      case 1:
        return <MenuProductsStep onNavigate={handleNavigateToConfig} />;
      case 2:
        return <OperationsStep onNavigate={handleNavigateToConfig} canNavigate={userCanManage} />;
      case 3:
        return <DeliveryTeamStep onNavigate={handleNavigateToConfig} canNavigate={userCanManage} />;
      case 4:
        return <RoutePricingStep />;
      case 5:
        return <IntegrationsStep onNavigate={handleNavigateToConfig} canNavigate={userCanManage} />;
      case 6:
        return <FiscalFinancialStep onNavigate={handleNavigateToConfig} canNavigate={userCanManage} />;
      case 7:
        return (
          <FinalStep
            onComplete={handleComplete}
            onNavigate={handleNavigateToConfig}
            canNavigate={userCanManage}
          />
        );
      default:
        return <WelcomeStep />;
    }
  };

  return (
    <AnimatedModal isOpen={isOpen} width="90%" maxWidth="1000px" zIndex={9999}>
      <ModalHeader title="Configuração Inicial" borderBottom={true} />

      {/* MUI Stepper */}
      <Box sx={{ px: 3, pt: 3, pb: 2 }}>
        <Stepper activeStep={activeStep} alternativeLabel={!isMobile}>
          {visibleSteps.map((step) => (
            <Step key={step.label}>
              <StepLabel>{!isMobile && step.label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      <ModalContent>
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={activeStep}
            custom={direction}
            variants={stepVariants}
            initial="enter"
            animate="center"
            exit="exit"
            style={{ width: "100%", height: "100%" }}
          >
            <StepContent>{renderStep()}</StepContent>
          </motion.div>
        </AnimatePresence>
      </ModalContent>

      <ModalFooter borderTop={true}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            gap: 2,
          }}
        >
          <Button
            onClick={handleBack}
            disabled={activeStep === 0}
            startIcon={<ArrowBackIcon />}
            sx={{ minWidth: 100 }}
          >
            Anterior
          </Button>

          <Button
            variant="text"
            onClick={handleSkip}
            sx={{ color: "text.secondary", display: { xs: "none", sm: "block" } }}
          >
            Pular Tutorial
          </Button>

          <ModernButton
            onClick={handleNext}
            endIcon={<ArrowForwardIcon />}
            sx={{ minWidth: 120 }}
          >
            {activeStep === visibleSteps.length - 1 ? "Finalizar" : "Próximo"}
          </ModernButton>
        </Box>
      </ModalFooter>
    </AnimatedModal>
  );
};

export default OnboardingGuidanceModal;
